import { Download, Smartphone, Share, Target, Mic, PenTool, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { lovable } from '@/integrations/lovable';
import { usePWAInstall } from '@/hooks/usePWAInstall';
import { useQuestionCount } from '@/hooks/useQuestionCount';
import { Link } from 'react-router-dom';
import { pickByLang } from '@/i18n';
import {
  ctaLabels,
  quickStartLabels,
  scorePlanLabels,
  trustLabels,
  headlineLabels,
  primaryCtaLabels,
} from './heroCopy';

const discoveryLinks: ReadonlyArray<{ to: string; labelKey: keyof import('@/i18n/ko').Translations }> = [
  { to: '/questions', labelKey: 'navQuestionPool' },
  { to: '/score-chart', labelKey: 'navScoreChart' },
  { to: '/guide', labelKey: 'guideLabel' },
  { to: '/pricing', labelKey: 'navPremium' },
  { to: '/faq', labelKey: 'navFaq' },
  { to: '/sitemap', labelKey: 'navSitemap' },
];

export default function HeroSection() {
  const { user } = useAuth();
  const { lang, t, localePath } = useI18n();
  const { canInstall, isInstalled, isIOS, install } = usePWAInstall();

  // Shared cache: TrustBadges and others reuse this same fetch (zero extra round-trips).
  const poolCount = useQuestionCount(3500);

  const handleGoogleLogin = async () => {
    await lovable.auth.signInWithOAuth('google', { redirect_uri: window.location.origin });
  };

  const n = poolCount.toLocaleString('en-US');
  const cl = pickByLang(ctaLabels, lang);
  const ql = pickByLang(quickStartLabels, lang);
  const sl = pickByLang(scorePlanLabels, lang);
  const tl = pickByLang(trustLabels, lang);
  const headline = pickByLang(headlineLabels(n), lang, 'en');
  const primary = pickByLang(primaryCtaLabels, lang, 'en');
  const sp = pickByLang({
    ko: { rating: '학습자 평점', questions: '실전 문제', languages: '개 언어 지원' },
    ja: { rating: '学習者評価', questions: '実戦問題', languages: '言語対応' },
    'zh-CN': { rating: '学员评分', questions: '真题练习', languages: '种语言' },
    'zh-TW': { rating: '學員評分', questions: '實戰題目', languages: '種語言' },
    vi: { rating: 'đánh giá', questions: 'câu hỏi', languages: 'ngôn ngữ' },
    th: { rating: 'คะแนนผู้เรียน', questions: 'ข้อสอบ', languages: 'ภาษา' },
    id: { rating: 'penilaian', questions: 'soal', languages: 'bahasa' },
    ms: { rating: 'penilaian', questions: 'soalan', languages: 'bahasa' },
    my: { rating: 'အဆင့်သတ်မှတ်ချက်', questions: 'မေးခွန်း', languages: 'ဘာသာစကား' },
    es: { rating: 'valoración', questions: 'preguntas', languages: 'idiomas' },
    'pt-BR': { rating: 'avaliação', questions: 'questões', languages: 'idiomas' },
    fr: { rating: 'note', questions: 'questions', languages: 'langues' },
    de: { rating: 'Bewertung', questions: 'Fragen', languages: 'Sprachen' },
    ar: { rating: 'تقييم المتعلمين', questions: 'أسئلة', languages: 'لغات' },
    en: { rating: 'learner rating', questions: 'practice questions', languages: 'languages' },
  }, lang, 'en');

  return (
    <section className="py-16 md:py-24">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <div>
          {/* Value-anchored headline: makes the price gap vs. ETS the lead message */}
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold tracking-wide mb-6">
            {t.heroBadge}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold font-display text-foreground mb-5 leading-tight break-keep">
            <span className="block">{headline.l1}</span>
            <span className="block text-accent">{headline.l2}</span>
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
            <span>{t.heroDesc1}</span>{' '}
            <span>{t.heroDesc2}</span>
          </p>
        </div>

        {/* Single primary CTA + small secondary links — fewer choices = higher conversion */}
        <div className="flex flex-col items-center justify-center gap-4 mb-6">
          <Link to={localePath('/mock-test')} className="w-full sm:w-auto">
            <Button size="lg" className="gap-2 rounded-2xl h-16 px-10 text-lg font-extrabold bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg shadow-accent/20 w-full sm:w-auto">
              <Target className="w-6 h-6" /> {primary}
            </Button>
          </Link>
          <div className="flex items-center gap-4 text-xs">
            <Link to={localePath('/questions')} className="text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
              {t.navQuestionPool}
            </Link>
            <span className="text-border">·</span>
            <Link to={localePath('/questions/speaking')} className="text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline inline-flex items-center gap-1">
              <Mic className="w-3 h-3" /> {cl.speaking}
            </Link>
            <span className="text-border">·</span>
            <Link to={localePath('/questions/writing')} className="text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline inline-flex items-center gap-1">
              <PenTool className="w-3 h-3" /> {cl.writing}
            </Link>
          </div>
        </div>

        {/* Social proof strip — factual, localized: learners + rating + languages */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-10 text-sm">
          <span className="inline-flex items-center gap-1.5">
            <span className="font-bold text-foreground">{n}+</span>
            <span className="text-muted-foreground">{sp.questions}</span>
          </span>
          <span className="text-border">·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="font-bold text-foreground">15</span>
            <span className="text-muted-foreground">{sp.languages}</span>
          </span>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8 max-w-3xl mx-auto">
          <Link to={localePath('/free-mock-test')} className="text-left rounded-2xl border border-border bg-card px-4 py-4 hover:border-accent/50 transition-colors">
            <p className="text-sm font-bold text-foreground mb-1">{ql.title}</p>
            <p className="text-xs text-muted-foreground mb-2">{ql.desc}</p>
            <span className="text-xs font-semibold text-accent">{ql.cta}</span>
          </Link>
          <Link to={localePath('/tips/score-guide')} className="text-left rounded-2xl border border-border bg-card px-4 py-4 hover:border-accent/50 transition-colors">
            <p className="text-sm font-bold text-foreground mb-1">{sl.title}</p>
            <p className="text-xs text-muted-foreground mb-2">{sl.desc}</p>
            <span className="text-xs font-semibold text-accent">{sl.cta}</span>
          </Link>
        </div>

        <p className="text-xs text-muted-foreground mb-8">{tl}</p>

        <div className="mb-8 flex flex-wrap items-center justify-center gap-2 text-xs">
          {discoveryLinks.map((link) => (
            <Link
              key={link.to}
              to={localePath(link.to)}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-accent/40 transition-colors"
            >
              {link.labelKey ? (t as unknown as Record<string, string>)[link.labelKey] : ''}
            </Link>
          ))}
        </div>


        {!user && lang !== 'zh-CN' && (
          <div className="flex items-center justify-center gap-3 mb-6 max-w-md mx-auto">
            <Button size="lg" onClick={handleGoogleLogin}
              className="bg-foreground text-background hover:bg-foreground/90 font-semibold px-8 py-5 gap-3 rounded-2xl h-auto text-base">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t.googleLogin}
            </Button>
          </div>
        )}

        {/* zh-CN: Email-first CTA since Google is blocked in China */}
        {!user && lang === 'zh-CN' && (
          <div className="flex items-center justify-center gap-3 mb-6 max-w-md mx-auto">
            <Link to={localePath('/pricing')}>
              <Button size="lg"
                className="bg-foreground text-background hover:bg-foreground/90 font-semibold px-8 py-5 gap-3 rounded-2xl h-auto text-base">
                <Mail className="w-5 h-5 flex-shrink-0" />
                邮箱注册，免费开始
              </Button>
            </Link>
          </div>
        )}

        {!isInstalled && (canInstall || isIOS) && (
          <div className="mt-4 max-w-md mx-auto">
            <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/5 via-primary/5 to-accent/10 p-4 sm:p-5">
              <div className="absolute top-0 right-0 w-24 h-24 bg-accent/10 rounded-full -translate-y-8 translate-x-8" />
              <div className="relative flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground">
                    {t.pwaAddToHome}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t.pwaAddToHomeDesc}
                  </p>
                </div>
                {canInstall ? (
                  <Button onClick={install} size="sm"
                    className="rounded-xl gap-1.5 font-bold flex-shrink-0 bg-accent text-accent-foreground hover:bg-accent/90">
                    <Download className="w-3.5 h-3.5" />
                    {t.pwaInstall}
                  </Button>
                ) : isIOS ? (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                    <Share className="w-3.5 h-3.5" />
                    <span>{t.pwaIOSGuide}</span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
